import '@babel/polyfill';
import 'prismjs';

import Vue from 'vue';
import axios from 'axios';
import PortalVue from 'portal-vue';
import VueTippy from 'vue-tippy';
import filters from 'common/filters';
import { Notifier } from 'common/plugins/Notifier';
import { LanguageService } from 'common/services/LanguageService';
import { VueApplication } from 'common/framework/applications/VueApplication';
import Tooltip from 'ui/v1/tooltip/Tooltip';

import ENV from '../config/env';
import { container } from 'tsyringe';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginVue from '@bugsnag/plugin-vue';
import { datadogLogs } from '@datadog/browser-logs';
import { Stonehenge } from '@jobilla/stonehenge-js-sdk';
import { FeatureToggleService } from '@jobilla/feature-toggles';

import i18n from './i18n';

function shouldReport(error) {
    // We exclude network errors and request failed errors from bugsnag reports but still handle them
    // e.g. log to console, etc.
    const requestFailedErr = error.message?.includes('Request failed');
    const networkErr = error.message?.includes('Network Error');
    const canceledErr = axios.isCancel(error) || error === 'canceled';
    if (canceledErr || requestFailedErr || networkErr) {
        return false;
    }

    return true;
}

if (['staging', 'production'].includes(ENV.APP_ENV)) {
    // Bugsnag integration
    Bugsnag.start({
        apiKey: ENV.BUGSNAG_API_KEY,
        releaseStage: ENV.APP_ENV,
        appVersion: ENV.APP_VERSION,
        autoTrackSessions: true,
        plugins: [new BugsnagPluginVue()],
        onError: report => shouldReport(report.originalError),
    });

    Bugsnag.getPlugin('vue')
        .installVueErrorHandler(Vue);

    datadogLogs.init({
        clientToken: ENV.DATADOG_TOKEN,
        site: 'datadoghq.eu',
        forwardErrorsToLogs: true,
        sampleRate: 100,
        service: 'Jobilla SPAs',
        env: ENV.APP_ENV,
    });
}
// Add Moment to global for access by all components
global.moment = require('moment');

// Application singleton
let _instance = null;

export default class JobillaSpaApplication extends VueApplication {
    constructor() {
        super();

        if (_instance) {
            return _instance;
        }

        _instance = this;

        this.appName = 'Jobilla Web Application';

        /**
         * Total of uncaught exceptions
         */
        this.errorCount = 0;

        this.notifier = new Notifier();

        // Register core plugins
        this.registerPlugins([this.notifier, PortalVue, VueTippy]);

        this.registerDirectives([
            Tooltip,
        ]);

        for (const name in filters) {
            Vue.filter(name, filters[name]);
        }

        // Init services
        this.i18n = i18n;

        this.languageService = new LanguageService(this.i18n);

        // Init the notifier
        this.notifier.i18n = this.i18n;

        /** @var {import('vuex').Store} */
        this.vuexStore = null;
        this.dataDogLogger = datadogLogs.logger;
        this.stonehengeLogger = Stonehenge.instance;
        this.ft = container.resolve(FeatureToggleService);
    }

    withStore(store) {
        this.vuexStore = store;

        return this;
    }

    start() {
        const config = { i18n: this.i18n };

        if (this.vuexStore) {
            config.store = this.vuexStore;
        }

        this.startApp(config);

        // Add the vue handler and a global window version
        Vue.config.errorHandler = err => this.handleError(err);
        window.onerror = err => this.handleError(err);
        window.addEventListener('unhandledrejection', (event) => {
            this.handleError(event.reason);
        });

        // Handler to warn not error log vue warnings
        Vue.config.warnHandler = (err, vm, info) => {
            // Suppress warnings about ApplicationView scopedSlots on slots
            if (vm && vm.constructor.options.name === 'ApplicationView'
                && err.toLowerCase()
                    .includes('duplicate presence of slot')) {
                return;
            }

            // It's okay to log to console in environments that we don't report to bugsnag in.
            if (!this.shouldReportToBugsnag()) {
                // eslint-disable-next-line no-console
                console.warn(`${err} ${info}`);
            }
        };
    }

    /**
     * Notify user of error and refresh.
     *
     * All errors are logged through bugsnag but the modal is only shown
     * for the first error that has occurred.
     *
     * @param {any} err
     * @returns
     *
     * @memberOf JobillaSpaApplication
     */
    handleError(err) {
        const senselessErr = !err;
        const resizeObserverLoopErr = (err === 'ResizeObserver loop limit exceeded' || err === 'ResizeObserver loop completed with undelivered notifications.');
        const JdsCancelErr = (err?.name === 'JdsError' && err?.type === 'cancel');

        if (senselessErr || resizeObserverLoopErr || JdsCancelErr || !shouldReport(err)) {
            return;
        }

        if (this.shouldReportBugToConsole()) {
            /* eslint-disable no-console */
            console.error(err);
        }

        if (!this.shouldReportToBugsnag()) {
            return this.notifier.toast('Caught JS Error - see console for details', { type: 'error' });
        }

        this.reportToBugsnag(err, null, { uncaughtError: true });

        this.errorCount++;

        // Only show toast for the first error
        if (this.errorCount > 1) {
            return;
        }

        return this.notifier.toast(
            'Oh no, something went wrong! An error report has been sent to engineering automatically.',
            { type: 'error' });
    }

    shouldReportToBugsnag() {
        return ['production', 'staging'].includes(ENV.APP_ENV);
    }

    shouldReportBugToConsole() {
        return !['production'].includes(ENV.APP_ENV);
    }

    reportToBugsnag(error, response = null, extra = {}) {
        if (!Bugsnag || !this.shouldReportToBugsnag()) {
            return;
        }
        // Add options to report to bugsnag
        Bugsnag.notify(error, (event) => {
            event.addMetadata('company', {
                companyId: this.currentCompanyId,
            });
            event.addMetadata('extra_context', extra);

            if (response) {
                const {
                    status,
                    responseURL,
                    statusText,
                } = response.request;
                event.request = {
                    status,
                    responseURL,
                    statusText,
                    errors: response.data.errors || 'no-errors-object',
                };
            }
        });
    }

    configureRoutes(routes, config) {
        super.configureRoutes(routes, config);
    }
}
